// src/app/features/products/productService.js
import axios from "axios";
import { base_url, getConfig } from "../../../utils/axiosConfig";

/* =========================================================
   Build Query String
========================================================= */
const buildQuery = (data = {}) => {
  const params = new URLSearchParams();

  if (data?.brand) params.set("brand", data.brand);
  if (data?.tag) params.set("tag", data.tag);
  if (data?.category) params.set("category", data.category);
  if (data?.slug) params.set("slug", data.slug);

  if (data?.minPrice !== null && data?.minPrice !== undefined && data?.minPrice !== "") {
    params.set("minPrice", data.minPrice);
  }

  if (data?.maxPrice !== null && data?.maxPrice !== undefined && data?.maxPrice !== "") {
    params.set("maxPrice", data.maxPrice);
  }

  if (data?.sort) params.set("sort", data.sort);

  const page = data?.page ?? 1;
  const limit = data?.limit ?? 100;
  params.set("page", String(page));
  params.set("limit", String(limit));

  const term = (data?.searchTerm ?? data?.search ?? data?.q ?? "").toString().trim();
  if (term) {
    params.set("searchTerm", term);
  }

  if (data?.fields) params.set("fields", data.fields);

  return params.toString();
};

/* =========================================================
   Normalize list from backend into array
========================================================= */
const normalizeList = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.products)) return payload.products;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload?.data?.products)) return payload.data.products;
  if (Array.isArray(payload.docs)) return payload.docs;
  if (Array.isArray(payload?.data?.docs)) return payload.data.docs;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.result)) return payload.result;

  return [];
};

/* =========================================================
   Extract pagination meta safely
========================================================= */
const extractMeta = (raw, listLen = 0) => {
  const root = raw ?? {};
  const meta = raw?.meta ?? {};
  const d = raw?.data ?? {};

  const total =
    meta?.total ??
    root?.total ??
    root?.results ??
    d?.total ??
    d?.results ??
    root?.totalDocs ??
    d?.totalDocs ??
    listLen;

  const page =
    meta?.page ??
    root?.page ??
    d?.page ??
    root?.currentPage ??
    d?.currentPage ??
    root?.pageNumber ??
    d?.pageNumber ??
    1;

  const limit =
    meta?.limit ??
    root?.limit ??
    d?.limit ??
    root?.perPage ??
    d?.perPage ??
    root?.pageSize ??
    d?.pageSize ??
    100;

  const pages =
    meta?.pages ??
    root?.pages ??
    d?.pages ??
    root?.totalPages ??
    d?.totalPages ??
    root?.pageCount ??
    d?.pageCount ??
    (limit > 0 ? Math.ceil(Number(total || 0) / Number(limit || 1)) : 0);

  const hasNextPage =
    meta?.hasNextPage ??
    root?.hasNextPage ??
    d?.hasNextPage ??
    Boolean(root?.nextPage ?? d?.nextPage);

  const hasPrevPage =
    meta?.hasPrevPage ??
    root?.hasPrevPage ??
    d?.hasPrevPage ??
    Boolean(root?.prevPage ?? d?.prevPage);

  return {
    total: Number(total || 0),
    page: Number(page || 1),
    limit: Number(limit || 100),
    pages: Number(pages || 0),
    hasNextPage: Boolean(hasNextPage),
    hasPrevPage: Boolean(hasPrevPage),
  };
};

/* =========================================================
   APIs
========================================================= */
const getProducts = async (data = {}) => {
  const qs = buildQuery(data);
  const url = `${base_url}product${qs ? `?${qs}` : ""}`;

  const response = await axios.get(url);

  const list = normalizeList(response.data);
  const meta = extractMeta(response.data, list.length);

  return {
    raw: response.data,
    products: list,
    meta,
  };
};



const getSingleProduct = async (slug) => {
  const safeSlug = String(slug || "").trim();

  const response = await axios.get(
    `${base_url}product/slug/${encodeURIComponent(safeSlug)}`
  );

  const product = response.data?.data ?? response.data;

  return {
    ...product,
    redirectTo: response.data?.redirectTo || product?.redirectTo || null,
  };
};

const addToWishlist = async (prodId) => {
  const response = await axios.put(
    `${base_url}product/wishlist`,
    { prodId },
    getConfig()
  );
  return response.data;
};

const rateProduct = async (data) => {
  const response = await axios.put(`${base_url}product/rating`, data, getConfig());
  return response.data;
};

export const productService = {
  getProducts,
  getSingleProduct,
  addToWishlist,
  rateProduct,
};